import { Country, State } from "country-state-city";
import { Form, Modal } from "react-bootstrap";
import { FaSpinner } from "react-icons/fa";
import "./application-modal.css";
import { hearAboutUsOptions } from "./partner-marketplace";

/**
 * ApplicationModal Component
 *
 * Form modal for users to apply for partner perks. Collects user information
 * (name, email, company, use case), how they heard about offer, and subscription details.
 * Validates form completeness before submission. Shows loading state during submission.
 *
 * @param {Object} props - Component props
 * @param {Function} props.handleChange - Form input change handler
 * @param {Function} props.handleSubmit - Form submission handler
 * @param {Function} props.handleHearAboutUsChange - Handler for "How did you hear" field
 * @param {Object} props.subscription - Current subscription state
 * @param {Function} props.setSubscription - Update subscription state
 * @param {boolean} props.showApplicationModal - Controls modal visibility
 * @param {Function} props.setShowApplicationModal - Toggle modal visibility
 * @param {Object} props.selectedPerk - Perk being applied for
 * @param {boolean} props.loading - Loading state during submission
 * @returns {JSX.Element} Application form modal
 */
const ApplicationModal = ({
  handleChange,
  handleSubmit,
  handleHearAboutUsChange,
  subscription,
  setSubscription,
  showApplicationModal,
  setShowApplicationModal,
  selectedPerk,
  loading,
}) => {
  const isFormValid = () => {
    // Basic required fields
    const basicFields = [
      "firstName",
      "lastName",
      "email",
      "job",
      "company",
      "LinkedIn",
      "contactNumber",
      "country",
      "state",
      "addressLine1",
      "city",
      "zipCode",
      // "designation",
      "knowYouBetter",
      "hearAboutUs",
    ];

    // Check basic fields
    const basicFieldsValid = basicFields.every((field) => subscription[field]);

    // Check other state if needed
    const otherStateValid =
      subscription.state !== "Other" || subscription.otherState;

    // Check dynamic hearAboutUs fields
    const hearAboutUsValid = !hearAboutUsOptions[
      subscription.hearAboutUs
    ]?.fields.some((field) => field.required && !subscription[field.name]);

    return basicFieldsValid && otherStateValid && hearAboutUsValid;
  };

  return (
    <Modal
      animation={false}
      show={showApplicationModal}
      onHide={() => setShowApplicationModal(false)}
      centered
      style={{
        fontFamily: `"AileronRegular", "OpenSansRegular"`,
      }}
      dialogClassName="custom-modal"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>{selectedPerk?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="getintouchcard">
        <div className="getintouchformheader">
          <div className="getintouchformbody">
            <div>
              <Form onSubmit={(e) => handleSubmit(e)}>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Form.Group
                    className="inputgroup"
                    controlId="formBasicFirstName"
                    style={{ width: "50%", paddingRight: "1.5%" }}
                  >
                    First name<sup>*</sup>
                    <Form.Control
                      disabled={loading}
                      onChange={handleChange}
                      required
                      maxLength="20"
                      className="inputbox"
                      type="text"
                      value={subscription.firstName}
                      name="firstName"
                      placeholder="Enter your first name"
                    />
                  </Form.Group>

                  <Form.Group
                    className="inputgroup"
                    controlId="formBasicLastName"
                    style={{ width: "50%" }}
                  >
                    Last name<sup>*</sup>
                    <Form.Control
                      disabled={loading}
                      onChange={handleChange}
                      required
                      maxLength="20"
                      className="inputbox"
                      type="text"
                      value={subscription.lastName}
                      name="lastName"
                      placeholder="Enter your last name"
                    />
                  </Form.Group>
                </div>
                <Form.Group className="inputgroup" controlId="formBasicEmail">
                  Email<sup>*</sup>
                  <Form.Control
                    disabled={loading}
                    onChange={handleChange}
                    pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$"
                    required
                    className="inputbox"
                    type="email"
                    value={subscription.email}
                    name="email"
                    placeholder="Enter your email address"
                  />
                </Form.Group>
                <Form.Group className="inputgroup" controlId="formBasicJob">
                  Job title (role)<sup>*</sup>
                  <Form.Control
                    disabled={loading}
                    onChange={handleChange}
                    required
                    className="inputbox"
                    type="text"
                    name="job"
                    value={subscription.job}
                    placeholder="Enter your job title or role"
                  />
                </Form.Group>
                <Form.Group className="inputgroup" controlId="formBasicCompany">
                  Company or organization name<sup>*</sup>
                  <Form.Control
                    disabled={loading}
                    onChange={handleChange}
                    required
                    className="inputbox"
                    type="text"
                    name="company"
                    value={subscription.company}
                    placeholder="Enter the name of your company or organization"
                  />
                </Form.Group>
                <Form.Group
                  className="inputgroup"
                  controlId="formBasicLinkedIn"
                >
                  LinkedIn profile (individual)<sup>*</sup>
                  <Form.Control
                    disabled={loading}
                    onChange={handleChange}
                    required
                    className="inputbox"
                    type="url"
                    name="LinkedIn"
                    value={subscription.LinkedIn}
                    placeholder="Enter your LinkedIn profile's URL"
                  />
                </Form.Group>
                <Form.Group className="inputgroup" controlId="formBasicPhone">
                  Mobile phone<sup>*</sup>
                  <Form.Control
                    disabled={loading}
                    onChange={handleChange}
                    required
                    className="inputbox"
                    type="tel"
                    name="contactNumber"
                    value={subscription.contactNumber}
                    placeholder="(+65) 8665 8665"
                  />
                </Form.Group>
                {/* Address Section */}
                <Form.Group className="inputgroup" controlId="formBasicCountry">
                  Country<sup>*</sup>
                  <Form.Select
                    disabled={loading}
                    onChange={(e) => {
                      const selectedCountry = Country.getAllCountries().find(
                        (c) => c.name === e.target.value,
                      );
                      setSubscription((prev) => ({
                        ...prev,
                        country: e.target.value,
                        countryCode: selectedCountry?.isoCode || "",
                        state: "",
                        otherState: "",
                      }));
                    }}
                    required
                    className="formbox"
                    value={subscription.country}
                    name="country"
                  >
                    <option value="">Select your country</option>
                    {Country.getAllCountries().map((country) => (
                      <option key={country.isoCode}>{country.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="inputgroup" controlId="formBasicState">
                  State<sup>*</sup>
                  <Form.Select
                    onChange={(e) =>
                      setSubscription((prev) => ({
                        ...prev,
                        state: e.target.value,
                        otherState: "",
                      }))
                    }
                    required
                    className="formbox"
                    value={subscription.state}
                    name="state"
                    disabled={!subscription.countryCode || loading}
                  >
                    <option value="">Select your state</option>
                    {State.getStatesOfCountry(subscription.countryCode).map(
                      (state) => (
                        <option key={state.isoCode}>{state.name}</option>
                      ),
                    )}
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
                {subscription.state === "Other" && (
                  <Form.Group
                    className="inputgroup"
                    controlId="formBasicOtherState"
                  >
                    Other (State)<sup>*</sup>
                    <Form.Control
                      disabled={loading}
                      onChange={(e) =>
                        setSubscription((prev) => ({
                          ...prev,
                          otherState: e.target.value,
                        }))
                      }
                      required
                      className="inputbox"
                      type="text"
                      value={subscription.otherState}
                      name="otherState"
                      placeholder="Please specify your state"
                    />
                  </Form.Group>
                )}
                <Form.Group
                  className="inputgroup"
                  controlId="formBasicAddressLine1"
                >
                  Address Line 1<sup>*</sup>
                  <Form.Control
                    disabled={loading}
                    onChange={handleChange}
                    required
                    className="inputbox"
                    type="text"
                    name="addressLine1"
                    value={subscription.addressLine1}
                    placeholder="Enter your address"
                  />
                </Form.Group>
                <Form.Group
                  className="inputgroup"
                  controlId="formBasicAddressLine2"
                >
                  Address Line 2
                  <Form.Control
                    disabled={loading}
                    onChange={handleChange}
                    className="inputbox"
                    type="text"
                    name="addressLine2"
                    value={subscription.addressLine2}
                    placeholder="Apartment, suite, etc."
                  />
                </Form.Group>
                <Form.Group className="inputgroup" controlId="formBasicCity">
                  City<sup>*</sup>
                  <Form.Control
                    disabled={loading}
                    onChange={handleChange}
                    required
                    className="inputbox"
                    type="text"
                    name="city"
                    value={subscription.city}
                    placeholder="Enter your city"
                  />
                </Form.Group>
                <Form.Group className="inputgroup" controlId="formBasicZip">
                  ZIP or Postal Code<sup>*</sup>
                  <Form.Control
                    disabled={loading}
                    onChange={handleChange}
                    required
                    className="inputbox"
                    type="text"
                    name="zipCode"
                    value={subscription.zipCode}
                    placeholder="ZIP or postal code"
                  />
                </Form.Group>
                {/* <Form.Group className="inputgroup" controlId="formBasicDesignation">
                                    Designation / Role<sup>*</sup>
                                    <Form.Control
                                        disabled={loading}
                                        onChange={handleChange}
                                        required
                                        className="inputbox"
                                        type="text"
                                        name="designation"
                                        value={subscription.designation}
                                        placeholder="Enter your designation or role"
                                    />
                                </Form.Group> */}
                We would like to know you better<sup>*</sup>
                <Form.Group className="inputgroup" controlId="formBasicAbout">
                  <Form.Select
                    disabled={loading}
                    onChange={handleChange}
                    required
                    className="formbox"
                    value={subscription.knowYouBetter}
                    name="knowYouBetter"
                    placeholder=""
                  >
                    <option></option>
                    <option>I'm an aspiring entrepreneur or founder</option>
                    <option>I'm an entrepreneur or founder</option>
                    <option>I'm from a startup</option>
                    <option>
                      I'm from a small-&-medium enterprise or business
                    </option>
                    <option>I'm from a scaleup</option>
                    <option>I'm from a corporation</option>
                    <option>I'm from an incubator or accelerator</option>
                    <option>I'm a researcher</option>
                    <option>I'm an aspiring developer or engineer</option>
                    <option>I'm a developer or engineer</option>
                    <option>
                      I'm an aspiring investor or emerging manager
                    </option>
                    <option>I'm an analyst</option>
                    <option>I'm an angel investor</option>
                    <option>I'm a venture capitalist (VC)</option>
                    <option>I'm a limited partner (LP)</option>
                    <option>I'm a hedge fund manager</option>
                    <option>I'm from an investment firm</option>
                    <option>I'm from an asset management firm</option>
                    <option>I'm from a private financial institution</option>
                    <option>I'm from a public financial institution</option>
                    <option>I'm an investment scout</option>
                    <option>I'm a broker or investment advisor</option>
                    <option>I'm a student</option>
                    <option>I'm a lecturer or professor</option>
                    <option>I'm from a school or university</option>
                    <option>I'm from the media</option>
                    <option>
                      I'm with the government or government authority
                    </option>
                    <option>
                      I'm from a state-owned enterprise or government-linked
                      company
                    </option>
                    <option>
                      I'm from a non-governmental or non-profit organization
                    </option>
                    <option>I'm just curious</option>
                  </Form.Select>
                </Form.Group>
                How did you hear about us?<sup>*</sup>
                <Form.Group className="inputgroup" controlId="formBasicAboutUs">
                  <Form.Select
                    disabled={loading}
                    onChange={handleHearAboutUsChange}
                    required
                    className="formbox"
                    value={subscription.hearAboutUs}
                    name="hearAboutUs"
                    placeholder=""
                  >
                    <option value="">Select</option>
                    {Object.keys(hearAboutUsOptions).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {hearAboutUsOptions[subscription.hearAboutUs]?.fields.map(
                  (field) => (
                    <Form.Group className="inputgroup mb-3" key={field.name}>
                      <Form.Label>{field.label}</Form.Label>
                      {field.type === "textarea" ? (
                        <Form.Control
                          disabled={loading}
                          as="textarea"
                          rows={3}
                          name={field.name}
                          value={subscription[field.name]}
                          onChange={(e) =>
                            setSubscription({
                              ...subscription,
                              [field.name]: e.target.value,
                            })
                          }
                          required={field.required}
                        />
                      ) : field.type === "select" ? (
                        <Form.Select
                          disabled={loading}
                          name={field.name}
                          value={subscription[field.name]}
                          onChange={(e) =>
                            setSubscription({
                              ...subscription,
                              [field.name]: e.target.value,
                            })
                          }
                          required={field.required}
                        >
                          <option value="">Select</option>
                          {field.options.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </Form.Select>
                      ) : field.type === "date" ? (
                        <Form.Control
                          disabled={loading}
                          type="date"
                          name={field.name}
                          value={subscription[field.name]}
                          onChange={(e) =>
                            setSubscription({
                              ...subscription,
                              [field.name]: e.target.value,
                            })
                          }
                          required={field.required}
                          onFocus={(e) => {
                            if (e.target.showPicker) e.target.showPicker();
                          }}
                        />
                      ) : (
                        <Form.Control
                          disabled={loading}
                          type={field.type}
                          name={field.name}
                          value={subscription[field.name]}
                          onChange={(e) =>
                            setSubscription({
                              ...subscription,
                              [field.name]: e.target.value,
                            })
                          }
                          required={field.required}
                        />
                      )}
                    </Form.Group>
                  ),
                )}
                <div className="extranote">
                  <span>
                    By clicking the 'Confirm & redeem' button below, you agree
                    to roiquant's{" "}
                    <a
                      href="https://viewpoints.roiquant.com/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      privacy policy
                    </a>
                    ,{" "}
                    <a
                      href="https://viewpoints.roiquant.com/how-do-we-use-cookies/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      cookies
                    </a>
                    ,{" "}
                    <a
                      href="https://viewpoints.roiquant.com/user-notice/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      user notice
                    </a>
                    ,{" "}
                    <a
                      href="https://roiquant.com/refund-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      refund policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://viewpoints.roiquant.com/terms-of-service/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      terms of service
                    </a>
                    .
                  </span>
                </div>
                <div style={{ textAlign: "center" }}>
                  <button
                    className="orderbutton glintanimation"
                    disabled={loading || !isFormValid()}
                    onClick={
                      isFormValid() && !loading ? handleSubmit : undefined
                    }
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="spin-icon" /> Loading...
                      </>
                    ) : selectedPerk?.actionLabel === "Apply now" ? (
                      "Confirm & apply"
                    ) : (
                      "Confirm & redeem"
                    )}
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Modal.Body>
      {/* <Modal.Footer>
                <Button
                    variant='primary'
                    onClick={handleSubmit}
                    className='glintanimation navbarideachecker'
                >
                    Apply Now
                </Button>
            </Modal.Footer> */}
    </Modal>
  );
};

export default ApplicationModal;
